/**
 * Synthetic camera, microphone and screen for the showcase surfaces.
 *
 * **Why this exists.** You cannot judge a media surface you cannot see media on.
 * The offline harness renders no media at all, and Chrome's
 * `--use-fake-device-for-media-stream` only fakes `getUserMedia` — it does
 * **not** fake `getDisplayMedia`, so a shared screen has never once been
 * observable in an automated run. Mirroring, `object-fit` cropping, frozen
 * tracks and card sizing are all invisible without a real stream, which is
 * exactly the class of defect that kept reaching the user by screenshot.
 *
 * So this installs sources at the `navigator.mediaDevices` boundary rather than
 * inside any one surface. Everything downstream — the SDK included, host page
 * and participant page alike — receives an ordinary `MediaStream` and needs no
 * knowledge that it is synthetic.
 *
 * **The sources are diagnostic, not filler.** Each one is drawn so that the
 * specific defect it guards is visible at a glance:
 *
 *  - the camera carries an asymmetric glyph and a `LEFT` edge label, so a
 *    mirrored tile is obvious and a self-view that stopped mirroring is too;
 *  - the screen carries markers in all four corners and fine text, so
 *    `object-fit: cover` cropping shows up as missing corners — `cover` slices
 *    the edges off slides and code, which is why the rule exists;
 *  - both carry a moving element and a frame counter, so a frozen track is
 *    distinguishable from a black one;
 *  - the microphone's amplitude rises and falls, so a level meter that never
 *    moves is distinguishable from one wired to nothing.
 */

const CAMERA_LABEL = 'MediaSFU synthetic camera';
const SCREEN_LABEL = 'MediaSFU synthetic screen';
const MIC_LABEL = 'MediaSFU synthetic microphone';

/** Frame rate for the canvas sources. Enough to read as live, cheap to draw. */
const FPS = 12;

/**
 * Whether synthetic media may be installed at all.
 *
 * Deliberately narrow: this ships in the same bundle as the public marketing
 * site, and a public page that can be switched into fake media is a page that
 * can be screenshotted as if it were real, and one where genuine breakage hides
 * behind a source that always works. Localhost and an explicit Dev build only.
 *
 * Widening this is a one-line change here — it is a single predicate on purpose
 * so the decision stays in one reviewable place.
 */
export function syntheticMediaAllowed() {
  if (typeof window === 'undefined') return false;
  try {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') return true;
    return process.env.REACT_APP_MODE === 'Dev';
  } catch (_) {
    return false;
  }
}

/** Survives a navigation to a URL that cannot carry the flag. */
export const SYNTHETIC_MEDIA_STORAGE_KEY = 'mediasfu:fakeMedia';

/**
 * `?fakeMedia=1` (or `=camera,mic` to narrow it). Absent means off.
 *
 * Also read from `sessionStorage`, because the query parameter cannot reach
 * every page that needs it. A participant invitation
 * (`/showcase-live?pack=…&reference=…`) is parsed against a strict allowlist
 * that **rejects the whole link** if any other key is present — deliberately, so
 * nothing can be smuggled across that boundary. Appending `&fakeMedia=1` to an
 * invite therefore does not enable synthetic media, it invalidates the
 * invitation. Session storage carries the intent across that navigation without
 * touching the URL.
 */
export function readSyntheticMediaRequest(search) {
  if (!syntheticMediaAllowed()) return null;
  let raw = '';
  try {
    const query = typeof search === 'string'
      ? search
      : (typeof window !== 'undefined' ? window.location.search : '');
    raw = new URLSearchParams(query).get('fakeMedia') || '';
    if (!raw && typeof window !== 'undefined') {
      raw = window.sessionStorage?.getItem(SYNTHETIC_MEDIA_STORAGE_KEY) || '';
    }
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  const on = (value) => ({ camera: value, mic: value, screen: value });
  if (raw === '1' || raw === 'true' || raw === 'all') return on(true);
  const wanted = new Set(raw.split(',').map((part) => part.trim().toLowerCase()).filter(Boolean));
  if (!wanted.size) return null;
  return {
    camera: wanted.has('camera') || wanted.has('video'),
    mic: wanted.has('mic') || wanted.has('audio'),
    screen: wanted.has('screen') || wanted.has('display'),
  };
}

function canvasSource(width, height, paint) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context || typeof canvas.captureStream !== 'function') {
    throw new Error('Canvas capture is unavailable in this browser.');
  }
  let frame = 0;
  const draw = () => { frame += 1; paint(context, canvas, frame); };
  draw();
  const timer = window.setInterval(draw, Math.round(1000 / FPS));
  const stream = canvas.captureStream(FPS);
  // A captured canvas track keeps drawing after every consumer is gone; stop the
  // painter when the track ends or the interval outlives the page.
  stream.getVideoTracks().forEach((track) => {
    const stop = track.stop.bind(track);
    track.stop = () => { window.clearInterval(timer); stop(); };
    track.addEventListener('ended', () => window.clearInterval(timer));
  });
  return stream;
}

/**
 * A camera you can tell the orientation of.
 *
 * The glyph and the `LEFT` label are the point: a self-view is supposed to
 * mirror and a remote tile is not, and with a symmetric test pattern both look
 * identical. This is what made "my self-view stopped mirroring when I shared my
 * screen" a thing you can see rather than reason about.
 */
export function createSyntheticCameraStream({ label = 'You', width = 640, height = 360 } = {}) {
  return canvasSource(width, height, (ctx, canvas, frame) => {
    const t = frame / FPS;
    ctx.fillStyle = '#0d1b2a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Moving element: a frozen track stops this dead.
    const cx = canvas.width / 2 + Math.cos(t) * (canvas.width * 0.22);
    const cy = canvas.height / 2 + Math.sin(t * 1.3) * (canvas.height * 0.16);
    ctx.fillStyle = '#38bdf8';
    ctx.beginPath();
    ctx.arc(cx, cy, 34, 0, Math.PI * 2);
    ctx.fill();

    // Asymmetric glyph — reads backwards the moment the tile is mirrored.
    ctx.fillStyle = '#f8fafc';
    ctx.font = `900 ${Math.round(canvas.height * 0.34)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('R', canvas.width / 2, canvas.height / 2);

    // Edge labels: on an unmirrored tile LEFT sits on the left.
    ctx.font = '700 18px system-ui, sans-serif';
    ctx.fillStyle = '#fbbf24';
    ctx.textAlign = 'left';
    ctx.fillText('LEFT', 14, canvas.height / 2);
    ctx.textAlign = 'right';
    ctx.fillText('RIGHT', canvas.width - 14, canvas.height / 2);

    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#94a3b8';
    ctx.font = '600 16px system-ui, sans-serif';
    ctx.fillText(`${CAMERA_LABEL} · ${label}`, 14, 26);
    ctx.fillText(`frame ${frame}`, 14, canvas.height - 14);
  });
}

/**
 * A screen share that shows you what a `cover` crop ate.
 *
 * The corner markers are load-bearing. A screen must use `object-fit: contain`;
 * with `cover` the edges are sliced off, and on a real slide that reads as
 * "slightly zoomed" rather than as broken. With a marker pinned in each corner,
 * a missing corner is unambiguous — and 16:10 against a 16:9 frame guarantees
 * the mismatch actually bites.
 */
export function createSyntheticScreenStream({ width = 1280, height = 800 } = {}) {
  return canvasSource(width, height, (ctx, canvas, frame) => {
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, canvas.width - 6, canvas.height - 6);

    const corners = [
      ['TL', 16, 16, 'left', 'top'],
      ['TR', canvas.width - 16, 16, 'right', 'top'],
      ['BL', 16, canvas.height - 16, 'left', 'bottom'],
      ['BR', canvas.width - 16, canvas.height - 16, 'right', 'bottom'],
    ];
    ctx.font = '900 30px system-ui, sans-serif';
    ctx.fillStyle = '#dc2626';
    corners.forEach(([text, x, y, align, baseline]) => {
      ctx.textAlign = align;
      ctx.textBaseline = baseline;
      ctx.fillText(text, x, y);
    });

    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#0f172a';
    ctx.font = '700 34px system-ui, sans-serif';
    ctx.fillText(SCREEN_LABEL, 60, 96);

    // Fine text: legible only if the share is not being downscaled or cropped,
    // which is the other half of what `contain` protects.
    ctx.fillStyle = '#334155';
    ctx.font = '15px ui-monospace, SFMono-Regular, Menlo, monospace';
    const lines = [
      'const room = await joinRoom({ name, token });',
      'room.on("producer", ({ id, kind }) => attach(id, kind));',
      '// a screen is never mirrored and never cropped',
      'video.style.objectFit = "contain";',
    ];
    lines.forEach((line, index) => ctx.fillText(line, 60, 150 + index * 26));

    // Moving bar so a frozen share is distinguishable from a still slide.
    ctx.fillStyle = '#16a34a';
    ctx.fillRect(60, canvas.height - 90, ((frame * 9) % (canvas.width - 120)), 14);
    ctx.fillStyle = '#64748b';
    ctx.font = '600 16px system-ui, sans-serif';
    ctx.fillText(`frame ${frame} · 16:10 source`, 60, canvas.height - 40);
  });
}

/**
 * A microphone that makes level meters move.
 *
 * A constant tone cannot tell "the meter is wired to nothing" from "the meter is
 * wired and the level is steady", so the gain is swept slowly instead.
 */
export function createSyntheticMicStream() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) throw new Error('WebAudio is unavailable in this browser.');
  const context = new Ctx();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const destination = context.createMediaStreamDestination();

  oscillator.type = 'sine';
  oscillator.frequency.value = 220;
  gain.gain.value = 0.08;
  oscillator.connect(gain);
  gain.connect(destination);
  oscillator.start();

  const started = context.currentTime;
  const sweep = window.setInterval(() => {
    const phase = (context.currentTime - started) % 4;
    gain.gain.setTargetAtTime(phase < 2 ? 0.14 : 0.02, context.currentTime, 0.3);
  }, 500);

  let graphStopped = false;
  const stopGraph = () => {
    if (graphStopped) return;
    graphStopped = true;
    window.clearInterval(sweep);
    try { oscillator.stop(); } catch (_) { /* already stopped */ }
    if (context.state !== 'closed') {
      try {
        const closing = context.close();
        closing?.catch?.(() => { /* another teardown path closed it first */ });
      } catch (_) { /* already closed */ }
    }
  };

  destination.stream.getAudioTracks().forEach((track) => {
    const stop = track.stop.bind(track);
    let trackStopped = false;
    track.stop = () => {
      stopGraph();
      if (trackStopped) return;
      trackStopped = true;
      stop();
    };
  });
  return destination.stream;
}

function labelTracks(stream, label) {
  stream.getTracks().forEach((track) => {
    try {
      Object.defineProperty(track, 'label', { value: label, configurable: true });
    } catch (_) { /* a read-only label is cosmetic, not worth failing over */ }
  });
  return stream;
}

const FAKE_DEVICES = Object.freeze([
  { deviceId: 'synthetic-camera', kind: 'videoinput', label: CAMERA_LABEL, groupId: 'synthetic' },
  { deviceId: 'synthetic-mic', kind: 'audioinput', label: MIC_LABEL, groupId: 'synthetic' },
  { deviceId: 'synthetic-speaker', kind: 'audiooutput', label: 'MediaSFU synthetic output', groupId: 'synthetic' },
]);

/**
 * Replaces the media entry points with synthetic sources.
 *
 * Returns an uninstall function. Installing twice is a no-op rather than a
 * double-wrap, so a re-render or a second route mount cannot nest the shims.
 */
export function installSyntheticMedia({ camera = true, mic = true, screen = true } = {}) {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return () => {};
  const devices = navigator.mediaDevices;
  if (devices.__mediasfuSyntheticInstalled) return devices.__mediasfuSyntheticUninstall || (() => {});

  // Raw references, not bound ones. Restoring a bound copy would leave
  // `navigator.mediaDevices.getUserMedia` a different function object than it
  // was before installing — an uninstall that does not actually undo itself.
  // Calls go through `.call(devices, …)` instead.
  const original = {
    getUserMedia: devices.getUserMedia,
    getDisplayMedia: devices.getDisplayMedia,
    enumerateDevices: devices.enumerateDevices,
  };
  const live = new Set();
  const track = (stream) => { live.add(stream); return stream; };

  if (camera || mic) {
    devices.getUserMedia = async (constraints = {}) => {
      const wantsVideo = Boolean(constraints.video) && camera;
      const wantsAudio = Boolean(constraints.audio) && mic;
      // A request this shim does not cover must still reach the real device
      // rather than silently returning an empty stream.
      if (!wantsVideo && !wantsAudio) {
        if (!original.getUserMedia) throw new Error('Media capture is unavailable.');
        return original.getUserMedia.call(devices, constraints);
      }
      const stream = new MediaStream();
      if (wantsVideo) {
        labelTracks(createSyntheticCameraStream(), CAMERA_LABEL)
          .getVideoTracks().forEach((t) => stream.addTrack(t));
      }
      if (wantsAudio) {
        // Audio prefers a real device track; the oscillator is the fallback.
        //
        // A WebAudio destination track is not the same thing as a microphone as
        // far as codec negotiation is concerned: these rooms carry supportSIP,
        // and the synthetic track ended up offering PCMU alone, which collided
        // on `mid:0` and made every audio `transport.produce` fail — 38 of them,
        // exclusively in harness rooms and never once for a real participant.
        //
        // That is a defect invented by the test rig, and it cost a long
        // investigation aimed at the product. Chrome's fake device (enabled with
        // --use-fake-device-for-media-stream) is a genuine device track and
        // negotiates like any real microphone, so the harness exercises the same
        // path a person does. Video stays synthetic: the drawn frames carry
        // labels and orientation that make rendering faults visible.
        let added = false;
        if (original.getUserMedia) {
          try {
            const real = await original.getUserMedia.call(devices, { audio: constraints.audio });
            real.getAudioTracks().forEach((t) => { stream.addTrack(t); added = true; });
          } catch (_) { added = false; }
        }
        if (!added) {
          labelTracks(createSyntheticMicStream(), MIC_LABEL)
            .getAudioTracks().forEach((t) => stream.addTrack(t));
        }
      }
      return track(stream);
    };
  }

  if (screen) {
    devices.getDisplayMedia = async () => track(
      labelTracks(createSyntheticScreenStream(), SCREEN_LABEL)
    );
  }

  devices.enumerateDevices = async () => {
    const real = original.enumerateDevices
      ? await original.enumerateDevices.call(devices).catch(() => [])
      : [];
    // Ours first: a picker that defaults to the first entry then defaults to a
    // source that actually produces frames.
    return [...FAKE_DEVICES, ...real];
  };

  const uninstall = () => {
    live.forEach((stream) => stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) { /* gone */ } }));
    live.clear();
    if (original.getUserMedia) devices.getUserMedia = original.getUserMedia;
    if (original.getDisplayMedia) devices.getDisplayMedia = original.getDisplayMedia;
    if (original.enumerateDevices) devices.enumerateDevices = original.enumerateDevices;
    delete devices.__mediasfuSyntheticInstalled;
    delete devices.__mediasfuSyntheticUninstall;
  };

  devices.__mediasfuSyntheticInstalled = true;
  devices.__mediasfuSyntheticUninstall = uninstall;
  return uninstall;
}

/**
 * Installs synthetic media if the URL asked for it and the build allows it.
 * Safe to call unconditionally; returns an uninstall function either way.
 */
export function installSyntheticMediaFromUrl(search) {
  const request = readSyntheticMediaRequest(search);
  if (!request) return () => {};
  // Persist the intent for the rest of the session, so following a participant
  // invitation from a faked host page stays faked — the invite URL itself cannot
  // carry the flag without being rejected by its own allowlist.
  try {
    const raw = new URLSearchParams(
      typeof search === 'string' ? search : window.location.search
    ).get('fakeMedia');
    if (raw) window.sessionStorage?.setItem(SYNTHETIC_MEDIA_STORAGE_KEY, raw);
  } catch (_) { /* storage is a convenience here, never a requirement */ }
  try {
    return installSyntheticMedia(request);
  } catch (_) {
    return () => {};
  }
}

export const SYNTHETIC_MEDIA_LABELS = Object.freeze({
  camera: CAMERA_LABEL,
  screen: SCREEN_LABEL,
  mic: MIC_LABEL,
});
